import * as THREE from 'three';
import type { InputState, PhysicsConfig, RacerState } from './Physics';
import { locateTrackPosition, type Track } from '../track/TrackLoader';

export type RaceMode = 'standard' | 'beginner';

const BEGINNER_MAX_SPEED_MULTIPLIER = 0.72;

export function buildPhysicsForMode(base: PhysicsConfig, mode: RaceMode): PhysicsConfig {
  if (mode === 'standard') return { ...base };
  return {
    ...base,
    maxSpeed: base.maxSpeed * BEGINNER_MAX_SPEED_MULTIPLIER,
    accel: base.accel * 0.9,
    brakeStrength: base.brakeStrength * 1.18,
    grip: base.grip * 1.22,
    turnRate: base.turnRate * 1.08,
    offTrackDrag: base.offTrackDrag * 0.65,
    offTrackGrip: base.offTrackGrip * 1.18,
    brakeGripMultiplier: base.brakeGripMultiplier * 1.08,
    lowSpeedSteerMultiplier: Math.max(base.lowSpeedSteerMultiplier, 0.72),
    highSpeedSteerMultiplier: Math.max(base.highSpeedSteerMultiplier, 0.72),
  };
}

export function applyBeginnerAssists(
  input: Readonly<InputState>,
  racer: RacerState,
  track: Track,
  physics: PhysicsConfig,
): InputState {
  const pos = locateTrackPosition(track, racer.position.x, racer.position.z);
  const speed = Math.hypot(racer.velocity.x, racer.velocity.z);
  const laneHalfWidth = Math.max(1, Math.min(pos.widthLeft, pos.widthRight));
  const lateral01 = THREE.MathUtils.clamp(Math.abs(pos.signedLateral) / laneHalfWidth, 0, 1);

  const lateralCorrection = THREE.MathUtils.clamp(
    pos.signedLateral / Math.max(1, laneHalfWidth * 0.9),
    -0.95,
    0.95,
  );
  const desiredX = pos.tx - pos.nx * lateralCorrection * 0.95;
  const desiredZ = pos.tz - pos.nz * lateralCorrection * 0.95;
  const desiredHeading = Math.atan2(desiredX, desiredZ);
  const headingError = normalizeRadians(desiredHeading - racer.heading);
  const assistSteer = THREE.MathUtils.clamp(-headingError * 0.95, -0.64, 0.64);

  const assistWeight = THREE.MathUtils.lerp(0.42, 0.76, lateral01);
  const steer = THREE.MathUtils.clamp(
    input.steer * 0.78 + assistSteer * assistWeight,
    -1,
    1,
  );

  const cornerNeed = Math.max(
    THREE.MathUtils.clamp(Math.abs(headingError) / 1.1, 0, 1),
    lateral01 * 0.75,
  );
  const targetSpeed = physics.maxSpeed * THREE.MathUtils.lerp(0.93, 0.56, cornerNeed);
  const needsBrake = speed > targetSpeed + 2;
  const assistedBrake = needsBrake
    ? THREE.MathUtils.clamp((speed - targetSpeed) / 18, 0, 0.72)
    : 0;
  const brake = Math.max(input.brake, assistedBrake);
  const autoThrottle = brake > 0.08
    ? THREE.MathUtils.lerp(0.18, 0.42, 1 - cornerNeed)
    : THREE.MathUtils.lerp(0.78, 0.92, 1 - cornerNeed);
  const throttle = Math.max(input.throttle, autoThrottle) * (brake > 0.5 ? 0.45 : 1);

  return { throttle, brake, steer };
}

export function isAssistedMode(mode: RaceMode): boolean {
  return mode === 'beginner';
}

function normalizeRadians(radians: number): number {
  return Math.atan2(Math.sin(radians), Math.cos(radians));
}
